import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { snooze_task_path } from "@/lib/routes";
import type { Task } from "@/pages/Tasks/task";

function isoInDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const PRESETS: Array<{ label: string; days: number }> = [
  { label: "Until tomorrow", days: 1 },
  { label: "For a week", days: 7 },
  { label: "For two weeks", days: 14 },
];

// "Remind me later": reminders pause, the task stays visible (paused is
// never hidden from supervisors). Reason is required — capturing why work
// is deliberately waiting is the feature, not a formality.
export default function PauseDialog({ task, open, onClose }: { task: Pick<Task, "id">; open: boolean; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [customDate, setCustomDate] = useState("");
  const ready = reason.trim().length > 0;

  // Both fields are local drafts — neither should outlive the dialog
  // instance they were typed into.
  useEffect(() => {
    setReason("");
    setCustomDate("");
  }, [open, task.id]);

  const pause = (untilDate: string) => {
    router.post(snooze_task_path(task.id), { until_date: untilDate, reason }, { preserveScroll: true });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Remind me later</DialogTitle>
        </DialogHeader>
        <label className="text-xs text-muted-foreground" htmlFor="pause-reason">What are you waiting on? (required)</label>
        <Input id="pause-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Waiting on price list" />
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((preset) => (
            <Button key={preset.days} variant="outline" size="sm" disabled={!ready} onClick={() => pause(isoInDays(preset.days))}>
              {preset.label}
            </Button>
          ))}
        </div>
        <label className="text-xs text-muted-foreground" htmlFor="pause-date">Or pick a date (max 30 days)</label>
        <div className="flex items-center gap-2">
          <Input id="pause-date" type="date" value={customDate} min={isoInDays(1)} max={isoInDays(30)} onChange={(e) => setCustomDate(e.target.value)} />
          <Button size="sm" disabled={!ready || !customDate} onClick={() => pause(customDate)}>Pause reminders</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
